import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Card,
  Grid,
  Typography,
  TableContainer,
  DialogActions,
  TextField,
  Modal,
  Paper,
  IconButton,
  Dialog,
  DialogContent,
  CircularProgress,
  DialogTitle,
} from "@mui/material";
import { StyledSnackbar, StyledAlert } from "components/StyledComponents/styledComponents";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import { AgGridReact } from "ag-grid-react";
import PropTypes from "prop-types";
import "ag-grid-community/styles/ag-grid.css"; // Core grid CSS
import "ag-grid-community/styles/ag-theme-alpine.css";
import dayjs from "dayjs";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { monthEndStyles } from "components/Styles/monthEndStyles";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { StyledAgGridContainer } from "components/StyledComponents/styledComponents";
import URLs from "constants/urls";
import { StyledButton } from "components/StyledComponents/styledComponents";
import { StyledCancelButton } from "components/StyledComponents/styledComponents";

const LeadReports = () => {
  const [fromDate, setFromDate] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [leadsList, setLeadsList] = useState([]);
  const [filteredLeadsList, setFilteredLeadsList] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const leadsGridRef = useRef(null);
  const [worklogData, setWorklogData] = useState([]);
  const [showWorklogPopup, setShowWorklogPopup] = useState(false);

  const setAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };
  const handleClose = () => setOpen(false);
  const viewLeadWorklog = async (leadId) => {
    setLoading(true);
    try {
      const response = await fetch(`${URLs.leadGetByIdUrl}/${leadId}`);
      if (!response.ok) throw new Error("Failed to fetch lead worklog");

      const data = await response.json();

      const mapped = data.map((log) => ({
        LogBy: log.LogBy || "Unknown",
        LogDate: log.LogDate || "",
        Comments: log.Comments || "No Comments",
      }));

      setWorklogData(mapped);
      setShowWorklogPopup(true);
    } catch (error) {
      setAlert("Error fetching worklog:", error);
      setLoading(false);
    }
    setLoading(false);
  };

  // 📅 Filter leads by Last Visit Date (inclusive range)
  const applyDateFilter = (list) => {
    if (!fromDate || !toDate) return list;
    const start = dayjs(fromDate).startOf("day");
    const end = dayjs(toDate).endOf("day");
    return list.filter((row) => {
      if (!row.LastVisitDate) return false;
      const visit = dayjs(row.LastVisitDate);
      return visit.isValid() && !visit.isBefore(start) && !visit.isAfter(end);
    });
  };

  const handleFilterSubmit = () => {
    if (!fromDate || !toDate) {
      setAlert("Please select both From and To dates.", "error");
      return;
    }
    setFilteredLeadsList(applyDateFilter(leadsList));
  };

  // 🔁 Fetch All Leads
  const fetchLeadsList = async () => {
    setLoading(true);
    try {
      const response = await fetch(URLs.leadGetALLUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: "",
        }),
      });
      if (!response.ok) throw new Error("Failed to fetch leads");

      const data = await response.json();

      setLeadsList(data);
      setFilteredLeadsList(applyDateFilter(data));
    } catch (error) {
      setAlert("Error fetching leads:", error);
    }
    setLoading(false);
  };
  useEffect(() => {
    fetchLeadsList();
  }, []);
  const ActionCellRenderer = (props) => {
    return (
      <div style={{ display: "flex", gap: "10px", alignItems: "center", justifyContent: "center" }}>
        <IconButton onClick={() => viewLeadWorklog(props.data.LeadId)}>
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </div>
    );
  };
  ActionCellRenderer.propTypes = {
    data: PropTypes.object.isRequired,
    context: PropTypes.shape({
      handleDateFetch: PropTypes.func.isRequired,
    }).isRequired,
  };

  // 📊 AG Grid Columns
  // const columnDefsLeads = [
  //   { headerName: "Customer Name", field: "CustomerName", flex: 1 },
  //   { headerName: "Mobile Number", field: "MobileNumber", flex: 1 },
  //   { headerName: "Loan Amount", field: "LoanAmount", flex: 1 },
  //   { headerName: "Loan Type", field: "LoanType", flex: 1 },
  //   { headerName: "Occupation", field: "Occupation", flex: 1 },
  //   { headerName: "Reference", field: "Reference", flex: 1 },
  //   { headerName: "Address", field: "Address", flex: 1 },
  //   { headerName: "Lead Date", field: "LeadDate", flex: 1 },
  //   { headerName: "Last Visit Date", field: "LastVisitDate", flex: 1 },
  //   { headerName: "Next Visit Date", field: "NextVisitDate", flex: 1 },
  //   { headerName: "Created By", field: "CreatedBy", flex: 1 },
  //   {
  //     headerName: "Actions",
  //     field: "actions",
  //     flex: 1,
  //     cellRenderer: ActionCellRenderer,
  //   },
  // ];

  const columnDefsLeads = [
    { headerName: "Customer Name", field: "CustomerName", flex: 1 },

    { headerName: "Mobile Number", field: "MobileNumber", flex: 1 },

    {
      headerName: "Loan Amount",

      field: "LoanAmount",

      flex: 1,

      // Converts abbreviations like "50L", "10Lacs" or plain numbers into "50,00,000"

      valueFormatter: (params) => {
        const numericValue = parseAmount(params.value);

        if (!numericValue) return params.value || "";

        return numericValue.toLocaleString("en-IN");
      },
    },

    { headerName: "Loan Type", field: "LoanType", flex: 1 },

    { headerName: "Occupation", field: "Occupation", flex: 1 },

    { headerName: "Reference", field: "Reference", flex: 1 },

    { headerName: "Address", field: "Address", flex: 1 },

    { headerName: "Lead Date", field: "LeadDate", flex: 1 },

    { headerName: "Last Visit Date", field: "LastVisitDate", flex: 1 },

    { headerName: "Next Visit Date", field: "NextVisitDate", flex: 1 },

    { headerName: "Created By", field: "CreatedBy", flex: 1 },

    {
      headerName: "Actions",

      field: "actions",

      flex: 1,

      cellRenderer: ActionCellRenderer,
    },
  ];

  // 💱 Currency columns whose totals should appear in the Excel footer
  const CURRENCY_FIELDS = ["LoanAmount"];

  // Loan Amount is entered as free text like "50lk", "2cr", "50 lk" — parse to a number for totals
  const parseAmount = (val) => {
    if (val === null || val === undefined || val === "") return 0;
    if (typeof val === "number") return val;
    const str = String(val).trim().toLowerCase().replace(/,/g, "");
    const match = str.match(/^([\d.]+)\s*(cr|crore|lk|lac|lakhs?|l)?$/);
    if (!match) {
      const n = parseFloat(str);
      return isNaN(n) ? 0 : n;
    }
    const num = parseFloat(match[1]);
    const unit = match[2];
    if (unit === "cr" || unit === "crore") return num * 10000000;
    if (unit && ["l", "lk", "lac", "lakh", "lakhs"].includes(unit)) return num * 100000;
    return num;
  };

  // 📥 Download Leads as Excel
  const downloadLeadsExcel = async () => {
    if (!filteredLeadsList.length) {
      setAlert("No data to download.", "error");
      return;
    }

    const exportColumns = columnDefsLeads.filter((col) => col.field !== "actions");

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Leads");

    worksheet.columns = exportColumns.map((col) => ({
      header: col.headerName,
      key: col.field,
    }));

    filteredLeadsList.forEach((row) => {
      const rowData = {};
      exportColumns.forEach((col) => {
        // Loan Amount is stored as free text like "50lk" / "2cr" — export the
        // parsed numeric value (e.g. 5000000) instead of the raw abbreviation,
        // matching what the grid on screen shows.
        rowData[col.field] = CURRENCY_FIELDS.includes(col.field)
          ? parseAmount(row[col.field])
          : row[col.field] ?? "";
      });
      worksheet.addRow(rowData);
    });

    // Header styling — bold, light grey background
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD9D9D9" },
      };
    });

    // Total footer row
    const totalRowData = {};
    exportColumns.forEach((col, idx) => {
      if (idx === 0) {
        totalRowData[col.field] = "Total";
      } else if (CURRENCY_FIELDS.includes(col.field)) {
        totalRowData[col.field] = filteredLeadsList.reduce(
          (sum, row) => sum + parseAmount(row[col.field]),
          0
        );
      } else {
        totalRowData[col.field] = "";
      }
    });
    const totalRow = worksheet.addRow(totalRowData);
    totalRow.font = { bold: true };
    totalRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD9D9D9" },
      };
    });

    // Auto-width columns
    worksheet.columns.forEach((column) => {
      let maxLength = column.header ? column.header.length : 10;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const cellLength = cell.value != null ? cell.value.toString().length : 0;
        maxLength = Math.max(maxLength, cellLength);
      });
      column.width = maxLength + 2;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: "application/octet-stream" }), "Leads_Report.xlsx");
  };

  const worklogColumnDefs = [
    { headerName: "Created By", field: "LogBy", width: 150 },
    {
      headerName: "Created Date",
      field: "LogDate",
      width: 200,
      valueFormatter: (params) => (params.value ? dayjs(params.value).format("DD-MMM-YYYY") : ""),
    },
    { headerName: "Comments", field: "Comments", flex: 1 },
  ];

  return (
    <DashboardLayout>
      <TableContainer component={Paper} sx={monthEndStyles.tableContainer}>
        <Box display="flex" justifyContent="flex-end" alignItems="center" sx={{ mb: 1 }}>
          <StyledButton onClick={downloadLeadsExcel}>
            <DownloadForOfflineIcon fontSize="medium" />
          </StyledButton>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={2}>
              <TextField
                type="date"
                label="From Date"
                fullWidth
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                type="date"
                label="To Date"
                fullWidth
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4} display="flex" alignItems="center">
              <StyledButton variant="contained" onClick={handleFilterSubmit}>
                Submit
              </StyledButton>
            </Grid>
          </Grid>
          <Dialog
            open={showWorklogPopup}
            fullWidth
            maxWidth="lg"
            onClose={() => setShowWorklogPopup(false)}
          >
            <DialogTitle sx={{ bgcolor: "#ffffff", color: "#121212" }}>Work Logs</DialogTitle>

            <DialogContent sx={{ bgcolor: "#ffffff", color: "#121212" }}>
              <Typography variant="caption" sx={{ fontSize: "14px", display: "block", mb: 1 }}>
                View all work logs made by the executive on the selected date.
              </Typography>

              <Box
                className="ag-theme-alpine"
                sx={{
                  height: 350,
                  width: "100%",
                  borderRadius: 1,
                  overflow: "auto",
                }}
              >
                <div
                  className="ag-theme-alpine"
                  style={{ height: 330, width: "100%", marginTop: 8 }}
                >
                  <AgGridReact
                    enableCellTextSelection={true}
                    enableRangeSelection={true}
                    rowData={worklogData}
                    columnDefs={worklogColumnDefs}
                    defaultColDef={{
                      filter: true, // Enable filtering by default
                      sortable: true, // Optional: allow sorting too
                      resizable: true, // Optional: allow resizing columns
                      floatingFilter: true,
                    }}
                    pagination
                    paginationPageSize={5}
                  />
                </div>
              </Box>
            </DialogContent>

            <DialogActions>
              <StyledCancelButton
                onClick={() => setShowWorklogPopup(false)}
                color="error"
                variant="contained"
              >
                Cancel
              </StyledCancelButton>
            </DialogActions>
          </Dialog>
        </Box>
        <StyledAgGridContainer
          component={Paper}
          className={`ag-theme-alpine`}
          sx={{
            flexGrow: 1,
            height: "100%",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            "& .ag-root-wrapper": {
              flex: 1,
              minHeight: 0,
            },
          }}
        >
          <AgGridReact
            enableCellTextSelection={true}
            enableRangeSelection={true}
            ref={leadsGridRef}
            rowData={filteredLeadsList}
            columnDefs={columnDefsLeads}
            frameworkComponents={{
              actionCellRenderer: ActionCellRenderer,
            }}
            defaultColDef={{
              filter: true, // Enable filtering by default
              sortable: true, // Optional: allow sorting too
              resizable: true, // Optional: allow resizing columns
              floatingFilter: true,
            }}
            pagination
            paginationPageSize={10}
          />
        </StyledAgGridContainer>
      </TableContainer>
      <Dialog
        open={loading}
        onClose={() => setLoading(false)}
        aria-labelledby="loading-dialog-title"
        sx={{
          "& .MuiDialog-paper": {
            padding: "clamp(10px, 2vw, 20px)",
            minWidth: "clamp(200px, 30vw, 300px)",
          },
        }}
      >
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "clamp(10px, 2vw, 20px)",
          }}
        >
          <CircularProgress color="primary" size="clamp(24px, 4vw, 40px)" />
          <Typography variant="h6" sx={{ fontSize: "clamp(14px, 2vw, 20px)" }}>
            Loading, please wait...
          </Typography>
        </DialogContent>
      </Dialog>
      <StyledSnackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        severity={alertType}
        sx={{
          width: "clamp(250px, 50vw, 400px)",
          top: "clamp(10px, 2vh, 20px)",
        }}
      >
        <StyledAlert onClose={handleClose} severity={alertType}>
          {message}
        </StyledAlert>
      </StyledSnackbar>
    </DashboardLayout>
  );
};

export default LeadReports;
